import React, { useMemo, useState } from "react";
import NumberShrink from "@/Components/NumberShrink";
import { useSelector } from "react-redux";
import ShowUsersList from "./ShowUsersList";
import { useTranslation } from "react-i18next";
import Like from "./Reactions/Like";
import Repost from "./Reactions/Repost";
import Quote from "./Reactions/Quote";
import Zap from "./Reactions/Zap";
import useCustomizationSettings from "@/Hooks/useCustomizationSettings";
import Icon from "@/Components/Icon";

export default function PostReaction({
  event,
  setOpenComment = () => null,
  setShowComments = () => null,
  openComment = false,
  postActions,
  userProfile,
}) {
  const { t } = useTranslation();
  const userKeys = useSelector((state) => state.userKeys);
  const [usersList, setUsersList] = useState(false);
  const { reactionsSettings } = useCustomizationSettings();
  const order = useMemo(() => {
    const reactionsOrder = reactionsSettings.reduce(
      (acc, { reaction, status }, index) => {
        acc[reaction] = { index, status };
        return acc;
      },
      {},
    );
    return {
      likes: reactionsOrder.likes.status ? reactionsOrder.likes.index + 1 : -1,
      replies: reactionsOrder.replies.status
        ? reactionsOrder.replies.index + 1
        : -1,
      repost: reactionsOrder.repost.status
        ? reactionsOrder.repost.index + 1
        : -1,
      quote: reactionsOrder.quote.status ? reactionsOrder.quote.index + 1 : -1,
      zap: reactionsOrder.zap.status ? reactionsOrder.zap.index + 1 : -1,
    };
  }, [reactionsSettings]);

  const isLiked = useMemo(() => {
    return userKeys
      ? postActions.likes.likes.find((item) => item.pubkey === userKeys.pub)
      : false;
  }, [postActions, userKeys]);

  const isReposted = useMemo(() => {
    return userKeys
      ? postActions.reposts.reposts.find((item) => item.pubkey === userKeys.pub)
      : false;
  }, [postActions, userKeys]);
  const isQuoted = useMemo(() => {
    return userKeys
      ? postActions.quotes.quotes.find((item) => item.pubkey === userKeys.pub)
      : false;
  }, [postActions, userKeys]);

  const isZapped = useMemo(() => {
    return userKeys
      ? postActions.zaps.zaps.find((item) => item.pubkey === userKeys.pub)
      : false;
  }, [postActions, userKeys]);

  return (
    <>
      {usersList && (
        <ShowUsersList
          exit={() => setUsersList(false)}
          title={usersList.title}
          list={usersList.list}
          extras={usersList.extras}
          extrasType={usersList.extrasType}
        />
      )}
      <div className="fx-centered" style={{ columnGap: "24px" }}>
        {order.likes > -1 && (
          <div
            className={`fx-centered pointer `}
            style={{ columnGap: "8px", order: order.likes }}
          >
            <Like
              isLiked={isLiked}
              event={event}
              actions={postActions}
              tagKind={event.kind > 30000 ? "a" : "e"}
            />
            <div
              className={`round-icon-tooltip ${isLiked ? "orange-c" : ""}`}
              data-tooltip={t("Alz0E9Y")}
              onClick={(e) => {
                e.stopPropagation();
                postActions.likes.likes.length > 0 &&
                  setUsersList({
                    title: t("Alz0E9Y"),
                    list: postActions.likes.likes.map((item) => item.pubkey),
                    extras: postActions.likes.likes,
                    extrasType: "reaction",
                  });
              }}
            >
              <div className={isLiked ? "" : "opacity-4"}>
                <NumberShrink value={postActions.likes.likes.length} />
              </div>
            </div>
          </div>
        )}
        {order.replies > -1 && (
          <div
            className={`fx-centered pointer `}
            style={{ columnGap: "8px", order: order.replies }}
          >
            <div className="round-icon-tooltip" data-tooltip={t("ADHdLfJ")}>
              <Icon
                name="comment"
                size={24}
                className="comment-24 opacity-4"
                onClick={() => setOpenComment(!openComment)}
              />
            </div>
            <div className="round-icon-tooltip" data-tooltip={t("AMBxvKP")}>
              <div onClick={() => setShowComments(true)} className="opacity-4">
                <NumberShrink value={postActions.replies.replies.length} />
              </div>
            </div>
          </div>
        )}
        {event.kind === 1 && order.repost > -1 && (
          <div
            className={`fx-centered pointer `}
            style={{ columnGap: "8px", order: order.repost }}
          >
            <Repost
              isReposted={isReposted}
              event={event}
              actions={postActions}
            />
            <div
              className={`round-icon-tooltip ${isReposted ? "orange-c" : ""}`}
              data-tooltip={t("Aai65RJ")}
              onClick={(e) => {
                e.stopPropagation();
                postActions.reposts.reposts.length > 0 &&
                  setUsersList({
                    title: t("Aai65RJ"),
                    list: postActions.reposts.reposts.map(
                      (item) => item.pubkey,
                    ),
                    extras: [],
                  });
              }}
            >
              <div className={isReposted ? "" : "opacity-4"}>
                <NumberShrink value={postActions.reposts.reposts.length} />
              </div>
            </div>
          </div>
        )}
        {order.quote > -1 && (
          <div
            className={`fx-centered pointer `}
            style={{ columnGap: "8px", order: order.quote }}
          >
            <Quote isQuoted={isQuoted} event={event} actions={postActions} />
            <div
              className={`round-icon-tooltip ${isQuoted ? "orange-c" : ""}`}
              data-tooltip={t("AWmDftG")}
              onClick={(e) => {
                e.stopPropagation();
                postActions.quotes.quotes.length > 0 &&
                  setUsersList({
                    title: t("AWmDftG"),
                    list: postActions.quotes.quotes.map((item) => item.pubkey),
                    extras: [],
                  });
              }}
            >
              <div className={isQuoted ? "" : "opacity-4"}>
                <NumberShrink value={postActions.quotes.quotes.length} />
              </div>
            </div>
          </div>
        )}
        {order.zap > -1 && (
          <div
            className="fx-centered"
            style={{ columnGap: "8px", order: order.zap }}
          >
            <div className="round-icon-tooltip" data-tooltip="Tip note">
              <Zap
                user={userProfile}
                event={event}
                actions={postActions}
                isZapped={isZapped}
              />
            </div>
            <div
              data-tooltip={t("AO0OqWT")}
              className={`pointer round-icon-tooltip ${
                isZapped ? "orange-c" : ""
              }`}
              onClick={() =>
                postActions.zaps.total > 0 &&
                setUsersList({
                  title: t("AVDZ5cJ"),
                  list: postActions.zaps.zaps.map((item) => item.pubkey),
                  extras: postActions.zaps.zaps,
                })
              }
            >
              <div className={isZapped ? "" : "opacity-4"}>
                <NumberShrink value={postActions.zaps.total} />
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
